import type { UseStore } from 'idb-keyval';
import { clear, createStore, entries, get, set, setMany } from 'idb-keyval';
import { MultiKey } from '../core/MultiKey';
import type {
  PortCostData,
  MarcuraPortCosts,
  MarcuraPortCostParams,
} from './context';
import { PortCostMap } from './portCostMap';
import type { LocationId } from '@seaber/common/core/entries';
import {
  MARCURA_PORT_COST_CACHE_TTL_MS,
  portCostPairKey,
} from '@seaber/common/lib/marcura';

let store: UseStore;

// Bump CACHE_VERSION on schema changes; on mismatch the store is cleared so
// users get a clean refetch instead of silently reading incompatible entries.
const CACHE_VERSION = 1;
const VERSION_KEY = '__version';

const isValidPortCostEntry = (value: unknown): value is PortCostData => {
  if (typeof value !== 'object' || value === null) return false;
  const cachedAt = (value as PortCostData).cached_at;
  return (
    typeof cachedAt === 'string' &&
    Number.isFinite(new Date(cachedAt).getTime())
  );
};

export function getPortCostIdb(): UseStore {
  if (!store) store = createStore('marcura-port-cost-cache', 'keyval');
  return store;
}

export async function clearPortCostCache(): Promise<void> {
  return await clear(getPortCostIdb());
}

export const getStoredPortCosts = async (): Promise<MarcuraPortCosts> => {
  const idb = getPortCostIdb();

  const storedVersion = await get<number>(VERSION_KEY, idb);
  if (storedVersion !== CACHE_VERSION) {
    if (storedVersion !== undefined) {
      await clear(idb);
    }
    await set(VERSION_KEY, CACHE_VERSION, idb);
  }

  const portCostEntries = await entries(idb);
  const storedPortCosts = new PortCostMap(
    new MultiKey<PortCostData, MarcuraPortCostParams>(2),
  );
  const map = storedPortCosts.map;

  const now = Date.now();

  for (const [key, value] of portCostEntries) {
    if (!value || !key || key === VERSION_KEY) continue;

    const [locationIdStr, assortmentIdStr] = key.toString().split('_');
    const locationId = Number(locationIdStr) as LocationId;
    const assortmentId = Number(assortmentIdStr);
    if (!Number.isFinite(locationId) || !Number.isFinite(assortmentId)) {
      continue;
    }

    if (!isValidPortCostEntry(value)) continue;
    if (
      now - new Date(value.cached_at).getTime() >
      MARCURA_PORT_COST_CACHE_TTL_MS
    ) {
      continue;
    }

    try {
      map.set(value, locationId, assortmentId);
    } catch (err) {
      console.error('port cost parsing failed', err);
    }
  }

  return storedPortCosts;
};

export const setStoredPortCosts = (datas: PortCostData[]): Promise<void> => {
  if (datas.length === 0) return Promise.resolve();
  const batch: [string, PortCostData][] = datas.map((data) => [
    portCostPairKey(data.location_id, data.assortment_id),
    data,
  ]);

  return setMany(batch, getPortCostIdb());
};
